// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventExplorerPage
 * @package @geenius/analytics/solidjs
 * @description Page-level SolidJS composition for filtering, inspecting, and
 * exporting analytics events.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import { buildAnalyticsSeoMeta } from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/solidjs";
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { EventTable } from "../components/EventTable";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { FadeIn } from "../lib/FadeIn";
import { PageErrorBoundary } from "./PageErrorBoundary";

const EVENT_TYPE_TRANSLATION_KEYS: Readonly<Record<EventType, string>> = {
  page_view: "analytics.events.types.pageView",
  click: "analytics.events.types.click",
  form_submit: "analytics.events.types.formSubmit",
  custom: "analytics.events.types.custom",
};

export interface EventExplorerFilters {
  types?: EventType[];
  startTime?: number;
  endTime?: number;
  url?: string;
  userId?: string;
  sessionId?: string;
  device?: DeviceInfo["device"];
  search?: string;
}

export type AnalyticsSeoMeta = ReturnType<typeof buildAnalyticsSeoMeta>;

/**
 * Props for rendering the SolidJS event explorer page.
 *
 * @property events Event rows available to browse and export.
 * @property initialFilters Optional initial filters for the local controls.
 * @property onFiltersChange Called whenever local filters change.
 * @property onExport Called with the current filtered event rows.
 * @property isLoading Whether the table should render loading placeholders.
 * @property error Optional page-level error message or error object.
 * @property onRetry Optional retry handler rendered with the error state.
 * @property maxRows Maximum number of table rows to render.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property class Optional class name merged into the page wrapper.
 */
export interface EventExplorerPageProps {
  events?: AnalyticsEvent[];
  initialFilters?: EventExplorerFilters;
  onFiltersChange?: (filters: EventExplorerFilters) => void;
  onExport?: (events: AnalyticsEvent[]) => void;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  maxRows?: number;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => unknown;
  class?: string;
}

const AnalyticsSeoHead: Component<{ meta: unknown }> = (props) => {
  void props.meta;
  return null;
};

function toDateInputValue(timestamp?: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().slice(0, 10);
}

function fromDateInputValue(
  value: string,
  boundary: "start" | "end",
): number | undefined {
  if (!value) return undefined;
  const date = new Date(
    `${value}T${boundary === "start" ? "00:00:00" : "23:59:59"}`,
  );
  return Number.isNaN(date.getTime()) ? undefined : date.getTime();
}

function includesText(value: string | undefined, query: string): boolean {
  return value?.toLowerCase().includes(query) ?? false;
}

function eventMatchesFilters(
  event: AnalyticsEvent,
  filters: EventExplorerFilters,
): boolean {
  if (filters.types?.length && !filters.types.includes(event.type)) {
    return false;
  }
  if (filters.startTime && event.timestamp < filters.startTime) return false;
  if (filters.endTime && event.timestamp > filters.endTime) return false;
  if (filters.device && event.device.device !== filters.device) return false;

  const urlQuery = filters.url?.trim().toLowerCase();
  if (urlQuery && !includesText(event.url, urlQuery)) return false;

  const userQuery = filters.userId?.trim().toLowerCase();
  if (userQuery && !includesText(event.userId, userQuery)) return false;

  const sessionQuery = filters.sessionId?.trim().toLowerCase();
  if (sessionQuery && !includesText(event.sessionId, sessionQuery)) {
    return false;
  }

  const searchQuery = filters.search?.trim().toLowerCase();
  if (searchQuery) {
    const searchable = [
      event.name,
      event.type,
      event.url,
      event.userId,
      event.sessionId,
      event.device.browser,
      event.device.os,
      JSON.stringify(event.properties ?? {}),
    ].join(" ");

    if (!searchable.toLowerCase().includes(searchQuery)) return false;
  }

  return true;
}

/**
 * Renders an event explorer with filters, property inspection, and export.
 *
 * @param props Event rows, initial filters, and export callback.
 * @returns A SolidJS page composition for event exploration workflows.
 */
export const EventExplorerPage: Component<EventExplorerPageProps> = (props) => {
  const [filters, setFilters] = createSignal<EventExplorerFilters>(
    props.initialFilters ?? {},
  );
  const [selectedEventId, setSelectedEventId] = createSignal("");
  const copy = createAnalyticsCopy();
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.eventExplorerTitle"),
    description: copy.t("analytics.seo.eventExplorerDescription"),
    path: "/analytics/events",
    keywords: ["analytics", "events", "filters", "export"],
  });
  const filteredEvents = createMemo(() =>
    (props.events ?? []).filter((event) =>
      eventMatchesFilters(event, filters()),
    ),
  );
  const inspectedEvent = createMemo(
    () =>
      filteredEvents().find((event) => event.id === selectedEventId()) ??
      filteredEvents()[0] ??
      null,
  );
  const updateFilters = (patch: Partial<EventExplorerFilters>): void => {
    const next = { ...filters(), ...patch };
    setFilters(next);
    props.onFiltersChange?.(next);
  };

  return (
    <>
      {props.renderSeoMeta ? (
        props.renderSeoMeta(seoMeta)
      ) : (
        <AnalyticsSeoHead meta={seoMeta} />
      )}
      <div
        class={cn(
          "flex min-h-screen flex-col gap-6 bg-background p-6 text-foreground",
          props.class,
        )}
        data-analytics-page="event-explorer"
      >
        <FadeIn>
          <header class="flex flex-col gap-1" data-analytics-page-header>
            <h1 class="text-3xl font-bold tracking-tight">
              {copy.t("analytics.events.explorerTitle")}
            </h1>
            <p class="text-sm text-muted-foreground">
              {copy.t("analytics.events.explorerSubtitle")}
            </p>
          </header>
        </FadeIn>

        <PageErrorBoundary error={props.error} onRetry={props.onRetry}>
          <FadeIn delay={60}>
            <Card data-analytics-event-filters>
              <CardHeader class="pb-0">
                <CardTitle data-analytics-section-title>
                  {copy.t("analytics.events.filters.title")}
                </CardTitle>
              </CardHeader>
              <CardContent class="grid gap-3 pt-4 sm:grid-cols-2 xl:grid-cols-4">
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.search")}</span>
                  <input
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    value={filters().search ?? ""}
                    onInput={(event) =>
                      updateFilters({ search: event.currentTarget.value })
                    }
                    placeholder={copy.t(
                      "analytics.events.filters.explorerSearchPlaceholder",
                    )}
                  />
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.type")}</span>
                  <select
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    value={filters().types?.[0] ?? ""}
                    onChange={(event) =>
                      updateFilters({
                        types: event.currentTarget.value
                          ? [event.currentTarget.value as EventType]
                          : undefined,
                      })
                    }
                  >
                    <option value="">
                      {copy.t("analytics.events.filters.allTypes")}
                    </option>
                    <For
                      each={
                        [
                          "page_view",
                          "click",
                          "form_submit",
                          "custom",
                        ] as EventType[]
                      }
                    >
                      {(type) => (
                        <option value={type}>
                          {copy.t(EVENT_TYPE_TRANSLATION_KEYS[type])}
                        </option>
                      )}
                    </For>
                  </select>
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.url")}</span>
                  <input
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    value={filters().url ?? ""}
                    onInput={(event) =>
                      updateFilters({ url: event.currentTarget.value })
                    }
                    placeholder={copy.t(
                      "analytics.events.filters.urlPlaceholder",
                    )}
                  />
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.user")}</span>
                  <input
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    value={filters().userId ?? ""}
                    onInput={(event) =>
                      updateFilters({ userId: event.currentTarget.value })
                    }
                    placeholder={copy.t(
                      "analytics.events.filters.userPlaceholder",
                    )}
                  />
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.session")}</span>
                  <input
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    value={filters().sessionId ?? ""}
                    onInput={(event) =>
                      updateFilters({ sessionId: event.currentTarget.value })
                    }
                    placeholder={copy.t(
                      "analytics.events.filters.sessionPlaceholder",
                    )}
                  />
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.device")}</span>
                  <select
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    value={filters().device ?? ""}
                    onChange={(event) =>
                      updateFilters({
                        device: event.currentTarget.value
                          ? (event.currentTarget.value as DeviceInfo["device"])
                          : undefined,
                      })
                    }
                  >
                    <option value="">
                      {copy.t("analytics.events.filters.allDevices")}
                    </option>
                    <option value="desktop">
                      {copy.deviceLabel("desktop")}
                    </option>
                    <option value="tablet">{copy.deviceLabel("tablet")}</option>
                    <option value="mobile">{copy.deviceLabel("mobile")}</option>
                  </select>
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.from")}</span>
                  <input
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    type="date"
                    value={toDateInputValue(filters().startTime)}
                    onInput={(event) =>
                      updateFilters({
                        startTime: fromDateInputValue(
                          event.currentTarget.value,
                          "start",
                        ),
                      })
                    }
                  />
                </label>
                <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                  <span>{copy.t("analytics.events.filters.to")}</span>
                  <input
                    class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    type="date"
                    value={toDateInputValue(filters().endTime)}
                    onInput={(event) =>
                      updateFilters({
                        endTime: fromDateInputValue(
                          event.currentTarget.value,
                          "end",
                        ),
                      })
                    }
                  />
                </label>
              </CardContent>
            </Card>
          </FadeIn>

          <div class="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)]">
            <FadeIn delay={120}>
              <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
                <span
                  class="text-sm font-medium text-muted-foreground"
                  data-analytics-event-count
                >
                  {copy.plural(
                    "analytics.events.resultCount",
                    filteredEvents().length,
                  )}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={filteredEvents().length === 0}
                  onClick={() => props.onExport?.(filteredEvents())}
                >
                  {copy.t("analytics.actions.export")}
                </Button>
              </div>
              <div
                data-analytics-event-table-loading={
                  props.isLoading || undefined
                }
              >
                <EventTable
                  events={filteredEvents()}
                  isLoading={props.isLoading}
                  maxRows={props.maxRows ?? 25}
                  showFilters={false}
                />
              </div>
            </FadeIn>

            <FadeIn delay={180}>
              <Card data-analytics-event-inspector>
                <CardHeader class="pb-0">
                  <CardTitle data-analytics-section-title>
                    {copy.t("analytics.events.inspector.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent class="grid gap-4 pt-4">
                  <Show
                    when={!props.isLoading}
                    fallback={
                      <>
                        <Skeleton class="h-9 w-full" />
                        <Skeleton class="h-36 w-full" />
                      </>
                    }
                  >
                    <Show
                      when={inspectedEvent()}
                      fallback={
                        <EmptyState
                          bordered={false}
                          compact
                          title={copy.t("analytics.events.inspector.empty")}
                        />
                      }
                    >
                      {(event) => (
                        <>
                          <label class="grid gap-1 text-xs font-medium text-muted-foreground">
                            <span>
                              {copy.t(
                                "analytics.events.inspector.selectedEvent",
                              )}
                            </span>
                            <select
                              class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                              value={event().id}
                              onChange={(inputEvent) =>
                                setSelectedEventId(
                                  inputEvent.currentTarget.value,
                                )
                              }
                            >
                              <For each={filteredEvents()}>
                                {(row) => (
                                  <option value={row.id}>{row.name}</option>
                                )}
                              </For>
                            </select>
                          </label>
                          <dl class="grid gap-3 text-sm">
                            <div class="grid gap-1">
                              <dt class="text-xs font-medium uppercase text-muted-foreground">
                                {copy.t("analytics.events.filters.session")}
                              </dt>
                              <dd>{event().sessionId}</dd>
                            </div>
                            <div class="grid gap-1">
                              <dt class="text-xs font-medium uppercase text-muted-foreground">
                                {copy.t("analytics.events.filters.user")}
                              </dt>
                              <dd>
                                {event().userId ??
                                  copy.t(
                                    "analytics.events.inspector.anonymous",
                                  )}
                              </dd>
                            </div>
                            <div class="grid gap-1">
                              <dt class="text-xs font-medium uppercase text-muted-foreground">
                                {copy.t("analytics.events.filters.device")}
                              </dt>
                              <dd>{copy.deviceLabel(event().device.device)}</dd>
                            </div>
                          </dl>
                          <pre class="max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-xs text-foreground">
                            {JSON.stringify(event().properties ?? {}, null, 2)}
                          </pre>
                        </>
                      )}
                    </Show>
                  </Show>
                </CardContent>
              </Card>
            </FadeIn>
          </div>
        </PageErrorBoundary>
      </div>
    </>
  );
};
